/**
 * ModalNavigator
 *
 * Renders content modals for articles, exercises, and videos.  Orchestrates
 * navigation between modals and opening up modals from within the current
 * modal, e.g. related videos from exercises.
 *
 * TODO(kevinb):
 * - remember which modal was open on reload (might be tricky)
 */
import {compose} from "mu-lambda";
import * as React from "react";
import {connect} from "react-redux";

import {
    withConversionContext,
    withMarkConversion,
    type PartialLearningTimeInfo,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {Attempt, ItemStatus, MasteryLevel} from "@khan/content-types";
import {Errors, KAError} from "@khan/logging";

import type {TaskInfo} from "#content-library/ui/content-renderers/exercise/store/store-types.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withRouter, {
    type WithoutRouter,
    type WithRouterProps,
} from "#data-access/with-router.ts";
import {
    getNewLevel,
    getNumCorrect,
} from "#mastery-utils-package/mastery-utils.ts";
import formatQueryString from "#shared-package/format-query-string.ts";
import {
    UPDATE_BEST_SCORE,
    UPDATE_LATEST_SCORE,
    UPDATE_STATUS,
} from "#user-info-store-package/user-info-constants.ts";
import userInfoStore from "#user-info-store-package/user-info-store.ts";

import {getNextItem, getPrevItem} from "../redux/modal-navigator-reducer.ts";
import modalNavigatorStore, {
    modalItemLearningTimeInfo,
} from "../redux/modal-navigator-store.ts";
import type {
    Action,
    ModalData,
    ModalItem,
    State as ModalNavigatorState,
} from "../redux/modal-navigator-types.ts";
import {stripModalFromRoute} from "../utils/modal-utils.ts";

const ArticleModal = React.lazy(
    () => import("../../components/modal-package/article-modal.tsx"),
);
const VideoModal = React.lazy(
    () => import("../../components/modal-package/video-modal.tsx"),
);
const PracticeTaskModal = React.lazy(() => import("./practice-task-modal.tsx"));
const InterspersedQuizModal = React.lazy(
    () => import("./stateful-interspersed-quiz-modal.tsx"),
);

// TODO(kevinb): find a good way to reuse user-info-store's getCurrentItem
const getCurrentItem = (
    modalData?: ModalData | null,
): ModalItem | null | undefined => {
    return (modalData && modalData.currentItem) || null;
};

type DefaultProps = {
    // Use for bigbingo conversion extra data in ExerciseModal
    inPractice: boolean;
    // Whether to show icon links to editor page the current content item
    showEditorShortcuts: boolean;
};

type OwnProps = DefaultProps & {
    // what is the URL we should redirect to when the modal closes?
    rootUrl?: string | null | undefined;
    // When true, this property tells the modal to parent in document.body.
    // This can help with situations where it doesn't render properly
    // directly in a document tree for whatever reason.
    hostInBody?: boolean;
    // Use for identifying the on-task state in ExerciseModal
    isOnTask?: boolean;
    // Callback for when the modal state changes: true when the modal
    // is displaying
    onChange?: (arg1: boolean) => unknown;
};

type ReduxProps = {
    // from redux
    modalData: ModalData | null | undefined;
    hasPrevItem: () => boolean;
    hasNextItem: () => boolean;
    nextItem: ModalItem | null | undefined;
    // When supplied, will return focus to this element after the last modal
    // closes
    focusId?: string;
    dispatch: (action: Action) => void;
};

type Props = OwnProps &
    WithLocationProps &
    WithRouterProps &
    WithMarkConversionProps &
    ReduxProps;

type State = {
    newLevel: MasteryLevel;
    pointsEarned: number;
    correctCount: number;
    oldLevel: MasteryLevel | undefined;
    taskInfo: TaskInfo | null | undefined;
};

class ModalNavigator extends React.Component<Props, State> {
    confettiContainer: HTMLElement | null | undefined;

    static defaultProps: DefaultProps = {
        inPractice: false,
        showEditorShortcuts: false,
    };

    state: State = {
        newLevel: "unfamiliar",
        pointsEarned: 0,
        correctCount: 0,
        oldLevel: undefined,
        taskInfo: null,
    };

    componentDidUpdate(prevProps: Props) {
        const {onChange} = this.props;

        if (!onChange) {
            return;
        }

        if (!prevProps.modalData && this.props.modalData) {
            onChange(true);
        } else if (prevProps.modalData && !this.props.modalData) {
            onChange(false);
        }
    }

    handleNext: () => void = () => {
        const {markConversion, dispatch, location} = this.props;
        markConversion({id: "content_modal_next"});
        dispatch({
            type: "NextItem",
            location,
        });
    };

    handlePrev: () => void = () => {
        const {markConversion, dispatch, location} = this.props;
        markConversion({id: "content_modal_prev"});
        dispatch({
            type: "PrevItem",
            location,
        });
    };

    closeItem: () => void = () => {
        const {
            history,
            location: {pathname, queryParams},
        } = this.props;

        if (!history) {
            modalNavigatorStore.dispatch({type: "CloseItem"});
            this.returnFocus();
            return;
        }

        const quizOrTest = !!pathname.match(/\/(quiz|test)\//);

        if (queryParams.modal || quizOrTest) {
            const parentPath = this.getRootPathWithoutModal();

            const newPath: string =
                parentPath ?? stripModalFromRoute(pathname, queryParams);

            history.push(newPath);
        } else {
            // For legacy modals, though, like the one in the practice tab, we
            // need to fire the original action to close the modal
            modalNavigatorStore.dispatch({type: "CloseItem"});
        }
        this.returnFocus();
    };

    getRootPathWithoutModal(): string | null | undefined {
        const {rootUrl, location} = this.props;
        if (!rootUrl) {
            return;
        }

        const [path] = rootUrl.split("?");

        // take the query parameters from the current URL and
        // preserve them, without the modal=1 part.
        const queryParams = location.queryParams;
        delete queryParams.modal;
        const queryString = formatQueryString(queryParams, true);

        return `${path}${queryString}`;
    }

    returnFocus() {
        const {focusId} = this.props;

        const focusElement = document.getElementById(focusId || "");
        if (focusElement) {
            focusElement.focus();
        }
    }

    handleShowQuizRelatedVideo: (
        taskData: any,
        videoData: {
            slug: string;
            contentId: string;
        },
    ) => void = (taskData, {slug, contentId}) => {
        const userExercises = taskData.userExercises;

        let relatedVideo = null;
        let videos: ReadonlyArray<ModalItem> = [];
        for (const userExercise of userExercises) {
            for (const video of userExercise.exerciseModel.relatedVideos) {
                if (video.nodeSlug === `v/${slug}`) {
                    relatedVideo = video;
                    videos = userExercise.exerciseModel.relatedVideos;
                    break;
                }
            }
        }

        if (!relatedVideo) {
            return;
        }

        modalNavigatorStore.dispatch({
            type: "OpenItem",
            modalData: {
                items: videos,
                referrer: "related_videos",
                currentItem: relatedVideo,
            },
        });
    };

    // TODO(kevinb) think about moving this to each of the modals
    handleUpdateProgress: (
        modalItem: ModalItem,
    ) => (status: ItemStatus) => void =
        (modalItem: ModalItem) => (status: ItemStatus) => {
            if (modalItem.kind === "InterspersedQuiz") {
                // Quizzes don't have progress
                return;
            }

            userInfoStore.dispatch({
                type: UPDATE_STATUS,
                progressKey: modalItem.progressKey,
                status,
            });
        };

    handleStartExercise: () => void = () => {};

    renderModals(): React.ReactNode {
        if (!this.props.modalData) {
            return null;
        }

        const {
            hasNextItem,
            hasPrevItem,
            hostInBody,
            isOnTask,
            modalData,
            nextItem,
        } = this.props;

        // TODO(kevinb): add a prop to the modals that don't modify the scroll
        // position because it's already been modified by another modal
        const {currentItem, referrer} = modalData;

        const commonProps = {
            key: currentItem.slug,
            onClose: this.closeItem,
            title: currentItem.title,
            hostInBody,
            isOnTask,

            // navigation props
            navigator: {
                onNext: this.handleNext,
                onPrev: this.handlePrev,
                hasNext: hasNextItem,
                hasPrev: hasPrevItem,
            },
            nextItem,

            // animation props
            visible: true,
            shouldAnimate: false,
            shouldBackdropDismiss: () => true,
            referrer,

            // header props
            linkToContent:
                referrer === "sat-related-content-widget" ? false : true,
        } as const;

        const handleUpdateProgressForCurrentItem =
            this.handleUpdateProgress(currentItem);

        if (currentItem.kind === "InterspersedQuiz") {
            const modal = (
                <React.Suspense fallback={null}>
                    <InterspersedQuizModal
                        {...commonProps}
                        extra={commonProps}
                        quiz={currentItem}
                        onNextTask={
                            referrer === "student-assignments-table"
                                ? this.handleNext
                                : () => {}
                        }
                    />
                </React.Suspense>
            );
            return modal;
        }
        if (currentItem.kind === "Exercise") {
            const {masteryMap} = userInfoStore.getState();

            let currentLevel = masteryMap[`e${currentItem.contentId}`];

            // NOTE(briang)
            // This can happen if we don't have a populated mastery
            // map. A valid reason for this is because the modal was
            // launched from an assignment, which doesn't display any
            // details about mastery outside the context of a unit.
            // If this changes in the future, we can do something like
            // in user-info-side-effects.js, where we populate the
            // mastery map based on the exercises in the assignment
            // page, but there isn't any value in doing that until we
            // need to display mastery in the assignments view of the
            // LIHP
            if (!currentLevel) {
                currentLevel = "unfamiliar";
            }

            const onTaskComplete = (
                attemptHistory: Array<Attempt>,
                taskInfo: TaskInfo,
                {
                    progressKey,
                }: {
                    progressKey: string;
                },
                pointsEarned: number,
                maybeNewLevel?: MasteryLevel | null,
            ) => {
                const isMasteryExercise =
                    currentItem.kind === "Exercise" &&
                    currentItem.masteryEnabled;

                const numCorrect = getNumCorrect(attemptHistory);
                const numAttempted = attemptHistory.length;
                const percent = numCorrect / numAttempted;

                const newLevel =
                    maybeNewLevel ||
                    getNewLevel(numCorrect, numAttempted, percent);

                if (isMasteryExercise) {
                    this.setState({
                        newLevel,
                        oldLevel: currentLevel,
                        pointsEarned,
                        correctCount: numCorrect,
                        taskInfo,
                    });
                }

                // NOTE(jvoll): If a learner is completing an assigned
                // contentItem on the logged-in home page,
                // progressKey will be undefined. From comments
                // describing UPDATE_BEST_SCORE, this action is used to
                // update the best score achieved for use on a topic
                // page. No point in dispatching incomplete info that
                // will not be used in the home-page case.
                if (progressKey) {
                    userInfoStore.dispatch({
                        type: UPDATE_BEST_SCORE,
                        progressKey,
                        bestScore: {
                            numCorrect,
                            numAttempted,
                            completedDate: new Date().toISOString(),
                        },
                    });

                    userInfoStore.dispatch({
                        type: UPDATE_LATEST_SCORE,
                        progressKey,
                        latestScore: {
                            numCorrect,
                            numAttempted,
                            completedDate: new Date().toISOString(),
                        },
                    });
                }
            };

            return (
                <React.Suspense fallback={null}>
                    <PracticeTaskModal
                        {...commonProps}
                        key={currentItem.slug}
                        exercise={currentItem}
                        onTaskComplete={onTaskComplete}
                        onCheckAnswer={(status) =>
                            handleUpdateProgressForCurrentItem(status)
                        }
                        onNextTask={this.handleNext}
                        showEditorShortcuts={this.props.showEditorShortcuts}
                    />
                </React.Suspense>
            );
        }
        if (currentItem.kind === "Video") {
            const {shouldAutoplay} = modalData;
            // TODO(kevinb) align this with what's on the server
            const video = {
                id: currentItem.contentId,
                kind: currentItem.kind,
                // NOTE(marksandstrom) It appears that nodeUrl is usually set
                // to relativeUrl, not kaUrl from the GraphQL type. Learning
                // time annotations for Test Prep are sniffed from this value.
                // See maybeGetTestPrepSubject called in video-modal.jsx.
                kaUrl: currentItem.nodeUrl,
                slug: currentItem.slug,
                translatedTitle: currentItem.title,
                translatedYoutubeId: currentItem.translatedYoutubeId,
                youtubeId: currentItem.youtubeId,
                recommended: currentItem.recommended,
            } as const;

            // TODO(kevinb) enable associated practice
            // See getPracticeForVideo in practice-module.jsx
            return (
                <React.Suspense fallback={null}>
                    <VideoModal
                        {...commonProps}
                        attribution={currentItem.attribution}
                        footer={
                            referrer === "sat-related-content-widget"
                                ? null
                                : undefined
                        }
                        shouldAutoplay={shouldAutoplay}
                        topicId={currentItem.topicId}
                        useBackIcon={false}
                        video={video}
                    />
                </React.Suspense>
            );
        }
        if (currentItem.kind === "Article") {
            return (
                <React.Suspense fallback={null}>
                    <ArticleModal
                        {...commonProps}
                        article={currentItem}
                        onArticleComplete={() =>
                            handleUpdateProgressForCurrentItem("complete")
                        }
                        topicId={currentItem.topicId}
                        hideFooter={referrer === "sat-related-content-widget"}
                    />
                </React.Suspense>
            );
        }
        throw new KAError(
            `unrecognized content kind ${currentItem.kind}`,
            Errors.Internal,
        );
    }

    render(): React.ReactNode {
        return <span>{this.renderModals()}</span>;
    }
}

// HACK (josh): `ownProps` needs to be provided, even though it's not used,
// because otherwise Redux optimizes this HOC and won't re-render when the
// route changes.
const mapStateToProps = (state: ModalNavigatorState, ownProps: OwnProps) => {
    const nextItem = getNextItem(state);
    const prevItem = getPrevItem(state);

    return {
        ...state,
        hasNextItem: () => !!nextItem,
        hasPrevItem: () => !!prevItem,
        nextItem,
    };
};

const mapPropsToLearningTime = (props: Props): PartialLearningTimeInfo => {
    const {modalData} = props;
    const current = getCurrentItem(modalData);
    if (!current) {
        return Object.freeze({});
    }
    return modalItemLearningTimeInfo(current);
};

type ExportProps = WithoutRouter<
    WithoutLocation<
        WithoutMarkConversion<
            Without<
                JSX.LibraryManagedAttributes<
                    typeof ModalNavigator,
                    React.ComponentProps<typeof ModalNavigator>
                >,
                ReduxProps
            >
        >
    >
>;

export default compose(
    withRouter,
    withLocation,
    connect(mapStateToProps),
    withConversionContext(mapPropsToLearningTime),
    withMarkConversion,
)(ModalNavigator) as React.ComponentType<ExportProps>;
